import { Heart, Planet, ShootingStar } from '@phosphor-icons/react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { APP_URL_CHAT, STORAGE_KEY_PENDING_ADVISOR_ALIAS, ZARA_ENABLED } from '../../../const';
import { ZARA_ADVISOR_ALIAS } from '../../../service/advisorAccess';
import { getUser, toggleAuthModal } from '../../../store/slices/authSlice';
import { fetchChats, getChatsState, startChatWithDefaultAdvisor } from '../../../store/slices/chatsSlice';
import { getZaraUpsellModalState, openPaymentModal, setShowZaraUpsellModal } from '../../../store/slices/modalsSlice';
import { gaEvents } from '../../../utils/analytics';
import CustomButton, { BUTTON_STYLE_TYPE } from '../Button';
import ZaraModalShell from '../ZaraModalShell';
import styles from './styles.module.scss';

// HORO-10504: value props (Figma layers are literally named Planet/Heart/
// ShootingStar — using those Phosphor icons directly rather than the
// bespoke SVG set LiveReadingsValueProps uses elsewhere). Shared by both
// variants below — generic "why Zara" copy, not viewer-specific.
const VALUE_PROPS = [
    { Icon: Planet, titleKey: 'zaraUpsell.valueProp1Title', descKey: 'zaraUpsell.valueProp1Desc' },
    { Icon: Heart, titleKey: 'zaraUpsell.valueProp2Title', descKey: 'zaraUpsell.valueProp2Desc' },
    { Icon: ShootingStar, titleKey: 'zaraUpsell.valueProp3Title', descKey: 'zaraUpsell.valueProp3Desc' }
];

// Full-screen upsell modal for the premium Zara advisor, opened from her
// locked catalog card / nav entries (see getAdvisorLockReason's REGISTER/
// SUBSCRIBE branches for Zara). Global/redux-controlled (showZaraUpsellModal
// in modalsSlice) rather than component-local, so every entry point (catalog
// card, sidebar/menu, chat-limit gate, marketing email) can open it the
// same way.
//
// HORO-10599: two variants of the same hero shell, chosen purely by whether
// the viewer is registered — by the time this is shown, a premium
// subscriber's tap never gets here at all (getAdvisorLockReason returns
// `null` for them), so "registered" here always means registered-free with
// an exhausted trial:
//   - register (anon): "Meet Zara!" — 3 free messages, register to start.
//   - subscribe (registered, trial exhausted): repurposed "Continue with
//     Zara" hero, same subscribe flow as before.
// The old anonymous "pick a plan, then register" resume (sessionStorage +
// AskWithoutLimitsPaywall) is gone — anon viewers register first now and
// use their free trial, rather than picking a plan before an account even
// exists.
const ZaraUpsellModal = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const isVisible = useSelector(getZaraUpsellModalState);
    const user = useSelector(getUser);
    const { list: chats } = useSelector(getChatsState);
    const isRegisterVariant = !user?.id;

    // HORO-10504: `isVisible` transitioning to true is "seen"; the variant
    // flag distinguishes the register pitch from the subscribe pitch, since
    // GA can't otherwise tell them apart.
    useEffect(() => {
        if (isVisible) {
            gaEvents.zaraUpsellModalViewed(isRegisterVariant);
            // Only the subscribe variant is a paywall; the register variant leads to
            // a free sign-up, and counting it would dilute zaraUpsell's conversion.
            if (!isRegisterVariant) gaEvents.paywallViewed('zaraUpsell');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible]);

    const onClose = () => {
        dispatch(setShowZaraUpsellModal(false));
    };

    // Register variant's CTA — same register flow every other REGISTER-
    // gated advisor uses (toggleAuthModal + the pending-alias stash), so
    // post-registration navigation lands the user straight into a chat with
    // her (see App.js's post-login redirect / MarketplaceActionButton).
    const onRegister = () => {
        gaEvents.zaraUpsellRegisterClicked();
        dispatch(setShowZaraUpsellModal(false));
        if (typeof sessionStorage !== 'undefined') {
            sessionStorage.setItem(STORAGE_KEY_PENDING_ADVISOR_ALIAS, ZARA_ADVISOR_ALIAS);
        }
        dispatch(toggleAuthModal({ trigger: true, step: 1, context: 'advisorLocked' }));
    };

    // Subscribe variant's CTA — this viewer is already registered (only a
    // trial-exhausted registered-free account reaches this branch), so this
    // dispatches straight into PaymentModal (mounted for logged-in users
    // only), content driven by their real plan state. openPaymentModal
    // tags this open so the shared PlanPaymentContent/PaymentButton's
    // eventual goToCheckout call is tagged `origin: 'zara'` too — that's
    // what lets PaymentSuccessPage send this subscriber into a chat with
    // Zara instead of its generic "back to chat" destination once payment
    // completes.
    const onSubscribe = () => {
        gaEvents.zaraUpsellSubscribeClicked(false);
        dispatch(setShowZaraUpsellModal(false));
        dispatch(openPaymentModal({ source: 'zaraUpsell', advisorAlias: ZARA_ADVISOR_ALIAS }));
    };

    // "Back to Chat" (subscribe variant only) — lets a trial-exhausted user
    // revisit their one existing Zara chat instead of losing it behind this
    // modal. Most-recent-chat resolution mirrors App.js's post-login
    // redirect / AnonHomePage's returning-user redirect: the backend
    // returns chats sorted newest-first, so the first entry with a
    // resolvable advisor+id IS the most recent chat. Falls back to starting
    // a fresh chat with the user's default advisor (same thunk
    // CreateNewChat's `default` type uses) on the rare chance they have
    // none yet.
    const onBackToChat = async () => {
        dispatch(setShowZaraUpsellModal(false));

        let chatList = chats;
        if (!chatList?.length) {
            try {
                const result = await dispatch(fetchChats({ reloading: true, page: 1 })).unwrap();
                chatList = result?.chats || [];
            } catch {
                chatList = [];
            }
        }

        const target = (chatList || []).find((chat) => chat?.advisor?.alias && chat?.id);
        if (target) {
            navigate(`/${APP_URL_CHAT}/${target.advisor.alias}/${target.id}`);
            return;
        }

        const result = await dispatch(startChatWithDefaultAdvisor());
        if (result?.meta?.requestStatus === 'fulfilled') {
            navigate(`/${APP_URL_CHAT}/${result.payload.advisor.alias}/${result.payload.id}`);
        }
    };

    // HORO-10504: kill switch — checked after every hook above (Rules of
    // Hooks) but before any render output. Mounted unconditionally in
    // App.js, so this is the one place that guarantees nothing Zara-shaped
    // renders regardless of what dispatched `setShowZaraUpsellModal(true)`.
    if (!ZARA_ENABLED) {
        return null;
    }

    const ValueProps = () => (
        <ul className={styles.valueProps}>
            {VALUE_PROPS.map(({ Icon, titleKey, descKey }) => (
                <li key={titleKey} className={styles.valueProp}>
                    <Icon className={styles.valuePropIcon} size={24} color="#FFFFFF" aria-hidden="true" />
                    <div className={styles.valuePropText}>
                        <span className={styles.valuePropTitle}>{t(titleKey)}</span>
                        <span className={styles.valuePropDesc}>{t(descKey)}</span>
                    </div>
                </li>
            ))}
        </ul>
    );

    if (isRegisterVariant) {
        return (
            <ZaraModalShell isVisible={isVisible} onClose={onClose}>
                <ValueProps />
                <p className={styles.subtext}>{t('zaraUpsell.registerSubtext')}</p>
                <div className={styles.ctas}>
                    <CustomButton
                        text={t('zaraUpsell.registerCta')}
                        onClick={onRegister}
                        styleType={BUTTON_STYLE_TYPE.PURPLE}
                        extraClasses={[styles.subscribeShimmer]}
                    />
                </div>
            </ZaraModalShell>
        );
    }

    return (
        <ZaraModalShell
            isVisible={isVisible}
            onClose={onClose}
            eyebrowKey="zaraUpsell.premiumEyebrow"
            titleLeadKey="zaraUpsell.continueTitle"
            titleAccentKey={null}
        >
            <ValueProps />
            <div className={styles.ctas}>
                <CustomButton
                    text={t('zaraUpsell.subscribeCta')}
                    onClick={onSubscribe}
                    styleType={BUTTON_STYLE_TYPE.PURPLE}
                    extraClasses={[styles.subscribeShimmer]}
                />
                <button type="button" className={styles.backToChatBtn} onClick={onBackToChat}>
                    {t('zaraUpsell.backToChat')}
                </button>
            </div>
        </ZaraModalShell>
    );
};

export default ZaraUpsellModal;
